import { ReactNode } from 'react';
import { Search, ChevronLeft, ChevronRight, Plus, RefreshCw } from 'lucide-react';
import { RefreshButton } from '@/components/ui/refresh-button';
import { getAvatarColors } from '@/lib/utils/avatar';
import {
  FilterableColumnHeader,
  FilterOption,
  DateRangePreset,
  DateRange,
  SortConfig,
} from '@/components/shared/DataTable';

/**
 * Generic Table View Component
 * Reusable table layout with consistent styling for lists (Patients, Practitioners, etc.)
 *
 * This component matches the exact styling of RecordsView for UI consistency.
 * Supports inline header filters - filters appear directly in column headers.
 */

export interface TableColumn<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Whether this column is sortable */
  sortable?: boolean;
  /** Sort key for API (defaults to column key) */
  sortKey?: string;
  /** Filter configuration for this column */
  filter?: {
    type: 'select' | 'date-range';
    options?: FilterOption[];
    /** Current filter value for select type */
    value?: string;
    /** Handler for filter changes */
    onChange?: (value: string | undefined) => void;
    /** For date range: current preset */
    datePreset?: DateRangePreset;
    /** For date range: custom range */
    dateRange?: DateRange;
    /** Handler for date range changes */
    onDateRangeChange?: (preset: DateRangePreset, range?: DateRange) => void;
  };
}

export interface TableViewProps<T> {
  // Header
  title: string;

  // Search
  search: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };

  // Add button
  addButton?: {
    label: string;
    onClick: () => void;
  };

  // Sort configuration (optional)
  sort?: {
    config: SortConfig;
    onSort: (key: string) => void;
  };

  // Table config
  columns: TableColumn<T>[];
  gridTemplate: string;

  // Data
  data: T[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;

  // States
  isLoading: boolean;
  isRefetching?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onRefresh?: () => void;
  emptyMessage?: string;
  emptySearchMessage?: string;

  // Pagination
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    hasPrev: boolean;
    hasNext: boolean;
    onPrev: () => void;
    onNext: () => void;
  };
}

export function TableView<T>({
  title,
  search,
  addButton,
  sort,
  columns,
  gridTemplate,
  data,
  getRowKey,
  onRowClick,
  isLoading,
  isRefetching,
  error,
  onRetry,
  onRefresh,
  emptyMessage = 'No data found.',
  emptySearchMessage = 'No results found matching your search.',
  pagination,
}: TableViewProps<T>) {
  const totalPages = Math.ceil(pagination.total / pagination.pageSize) || 1;

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-[#e1e3ed]">
      <div className="flex flex-1 flex-col py-6">
        {/* Header Section - Title, Search, Button */}
        <div className="flex items-center justify-between gap-4">
          {/* Title */}
          <h1 className="flex-shrink-0 font-hanken text-[calc(24px*var(--app-scale))] font-medium leading-[calc(28px*var(--app-scale))] tracking-normal text-gray-700">
            {title}
          </h1>

          {/* Search and Button - Right aligned */}
          <div className="flex items-center gap-3">
            {/* Search Bar */}
            <div className="relative w-[calc(280px*var(--app-scale))]">
              <Search className="absolute left-3 top-1/2 h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] -translate-y-1/2 text-gray-700" />
              <input
                type="text"
                placeholder={search.placeholder || 'Search'}
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                className="w-full rounded-full bg-white py-2 pl-10 pr-4 font-hanken text-[calc(16px*var(--app-scale))] font-medium text-gray-700 shadow-sm placeholder:text-[#bcc0d7] focus:outline-none focus:ring-2 focus:ring-[#061a61]/20"
              />
            </div>

            {/* Add Button */}
            {addButton && (
              <button
                onClick={addButton.onClick}
                className="flex h-9 items-center gap-1 rounded-lg bg-[#061a61] px-3 font-hanken text-[calc(16px*var(--app-scale))] font-medium text-white hover:bg-[#0a2480]"
              >
                <Plus className="h-[calc(16px*var(--app-scale))] w-[calc(16px*var(--app-scale))]" />
                {addButton.label}
              </button>
            )}
          </div>
        </div>

        {/* Table Section */}
        <div className="mt-4 flex flex-col">
          <div className="relative rounded-3xl bg-white shadow-sm">
            {/* Refresh Button - positioned in top right corner of table */}
            {onRefresh && (
              <RefreshButton
                onClick={onRefresh}
                isLoading={isLoading}
                className="absolute right-4 top-3"
              />
            )}
            <div className="overflow-x-auto">
              {/* Table Header with inline filters */}
              <div
                className={`grid ${gridTemplate} min-w-max items-start gap-4 whitespace-nowrap border-b border-gray-200 px-6 py-4`}
              >
                {columns.map((col) => (
                  <FilterableColumnHeader
                    key={col.key}
                    sortable={col.sortable}
                    sortKey={col.sortKey || col.key}
                    currentSort={sort?.config}
                    onSort={sort?.onSort}
                    filterable={!!col.filter}
                    filterType={col.filter?.type}
                    filterOptions={col.filter?.options}
                    filterValue={col.filter?.value}
                    onFilterChange={col.filter?.onChange}
                    datePreset={col.filter?.datePreset}
                    dateRange={col.filter?.dateRange}
                    onDateRangeChange={col.filter?.onDateRangeChange}
                  >
                    {col.header}
                  </FilterableColumnHeader>
                ))}
              </div>

              {/* Table Body */}
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="h-[calc(32px*var(--app-scale))] w-[calc(32px*var(--app-scale))] animate-spin rounded-full border-4 border-[#6b8af9] border-t-transparent" />
                </div>
              ) : isRefetching ? (
                <div className="flex min-w-max flex-col items-center justify-center py-20">
                  <RefreshCw className="h-10 w-10 animate-spin text-[#6b8af9]" />
                  <p className="mt-3 font-hanken text-sm text-gray-500">Refreshing...</p>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <p className="font-hanken text-[calc(16px*var(--app-scale))] text-red-500">
                    {error}
                  </p>
                  {onRetry && (
                    <button
                      onClick={onRetry}
                      className="mt-4 rounded-md bg-[#6B8AF9] px-4 py-2 text-[calc(16px*var(--app-scale))] text-white hover:bg-[#566EC7]"
                    >
                      Retry
                    </button>
                  )}
                </div>
              ) : data.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                  <p className="font-hanken text-[calc(16px*var(--app-scale))] text-gray-700">
                    {search.value ? emptySearchMessage : emptyMessage}
                  </p>
                </div>
              ) : (
                <div className="min-w-max divide-y divide-gray-100">
                  {data.map((row) => (
                    <div
                      key={getRowKey(row)}
                      onClick={() => onRowClick?.(row)}
                      className={`grid ${gridTemplate} items-center gap-4 whitespace-nowrap px-6 py-1.5 hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                    >
                      {columns.map((col) => (
                        <div key={col.key}>{col.render(row)}</div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pagination - Fixed at bottom right */}
        {!isLoading && pagination.total > 0 && (
          <div className="fixed bottom-4 right-6">
            <div className="flex items-center gap-4 rounded-full bg-[#e6e8ef] px-4 py-2 shadow-md">
              <button
                onClick={pagination.onPrev}
                disabled={!pagination.hasPrev}
                className="flex h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] items-center justify-center disabled:opacity-50"
              >
                <ChevronLeft className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-[#42434b]" />
              </button>
              <span className="font-hanken text-[calc(16px*var(--app-scale))] font-medium text-[#42434b]">
                {pagination.page} out of {totalPages}
              </span>
              <button
                onClick={pagination.onNext}
                disabled={!pagination.hasNext}
                className="flex h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] items-center justify-center disabled:opacity-50"
              >
                <ChevronRight className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-[#42434b]" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Helper component for avatar with name cell
 * Matches RecordsView avatar styling exactly
 */
export interface AvatarCellProps {
  name: string;
  subtitle?: string | null;
}

export function AvatarCell({ name, subtitle }: AvatarCellProps) {
  const colors = getAvatarColors(name);
  return (
    <div className="flex items-center gap-3">
      <div
        className="flex h-[calc(40px*var(--app-scale))] w-[calc(40px*var(--app-scale))] flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/50"
        style={{ backgroundColor: colors.bg }}
      >
        <span
          className="font-hanken text-[calc(16px*var(--app-scale))] font-medium"
          style={{ color: colors.text }}
        >
          {name.charAt(0).toUpperCase()}
        </span>
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="truncate font-hanken text-[calc(16px*var(--app-scale))] font-medium text-gray-700">
          {name}
        </p>
        {subtitle && (
          <p className="truncate font-hanken text-[calc(13px*var(--app-scale))] text-[#888]">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Helper component for status badge
 */
export interface StatusBadgeProps {
  status: 'active' | 'inactive' | 'deceased' | string;
}

const statusStyles: Record<string, string> = {
  active: 'text-green-600',
  inactive: 'text-gray-500',
  deceased: 'text-red-500',
};

export function StatusBadge({ status }: StatusBadgeProps) {
  return (
    <p
      className={`font-hanken text-[calc(16px*var(--app-scale))] font-medium ${statusStyles[status] || 'text-gray-700'}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </p>
  );
}

/**
 * Helper component for centered text cell
 */
export function TextCell({ children }: { children: ReactNode }) {
  return (
    <div className="text-center">
      <p className="font-hanken text-[calc(16px*var(--app-scale))] font-medium text-gray-700">
        {children}
      </p>
    </div>
  );
}

export default TableView;
